import { useMemo } from 'react';

import { useQuery } from '@apollo/client/react';

import { STATE_BY_COORDINATES } from '@modules/reports/cma-reports/cmaReportsQueries';

type UseStateByCoordinatesResult = {
  stateId: string | null;
  stateName: string | null;
  loading: boolean;
};

export const useStateByCoordinates = (
  lat: number | null | undefined,
  lng: number | null | undefined,
  countryCode: string | null | undefined,
): UseStateByCoordinatesResult => {
  const hasCoordinates = lat != null && lng != null;
  const validCountryCode = countryCode?.toUpperCase() ?? '';

  const point = useMemo(() => {
    if (!hasCoordinates) {
      return null;
    }
    return {
      type: 'Point',
      coordinates: [lng, lat],
    };
  }, [hasCoordinates, lat, lng]);

  const { data, loading } = useQuery(STATE_BY_COORDINATES, {
    skip: !hasCoordinates || !validCountryCode,
    variables: {
      point,
      isCh: validCountryCode === 'CH',
      isFr: validCountryCode === 'FR',
      isEs: validCountryCode === 'ES',
      isIt: validCountryCode === 'IT',
      isPl: validCountryCode === 'PL',
      isPt: validCountryCode === 'PT',
    },
    context: { clientName: 'scrapers' },
  });

  return useMemo(() => {
    if (!data) {
      return { stateId: null, stateName: null, loading };
    }

    const places =
      data.ch_places?.[0] ??
      data.fr_places?.[0] ??
      data.es_places?.[0] ??
      data.it_places?.[0] ??
      data.pl_places?.[0] ??
      data.pt_places?.[0];

    if (places) {
      return { stateId: places.id, stateName: places.name, loading };
    }

    return { stateId: null, stateName: null, loading };
  }, [data, loading]);
};
